import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaPaperPlane, FaCheckCircle, FaExclamationCircle, FaSpinner, FaDraftingCompass, FaHardHat, FaHome, FaChevronDown, FaWindowMaximize } from 'react-icons/fa'
import MondayManagement from '../pages/admin/MondayManagement'
import ConsentCheckbox from '../components/ConsentCheckbox'

const FORM_THANK_YOU_MAP = {
    estimados_2026_new_app: '/forms/architecture/new-quote/thank-you',
    estimados_2026_new_scope: '/forms/architecture/new-scope/thank-you',
    construction_new_project: '/forms/construction/new-quote/thank-you',
    roofing_new_project: '/forms/roofing/new-quote/thank-you',
    impact_windows_new_project: '/forms/impact-windows/new-quote/thank-you',
}

const ContactPage = () => {
    const { t } = useTranslation()
    const [activeMap, setActiveMap] = useState('texas') // 'texas' or 'florida'
    const [showMondayForm, setShowMondayForm] = useState(false)
    const [activeFormType, setActiveFormType] = useState(null)
    const [expandedBU, setExpandedBU] = useState(null)
    const preModalPath = useRef(null)

    const FORM_PATHS = {
        estimados_2026_new_app: '/forms/architecture/new-quote',
        construction_new_project: '/forms/construction/new-quote',
        roofing_new_project: '/forms/roofing/new-quote',
        impact_windows_new_project: '/forms/impact-windows/new-quote',
    }

    const BU_CONFIG = [
        { key: 'architecture', formType: 'estimados_2026_new_app', Icon: FaDraftingCompass, nameKey: 'nav.architecture' },
        { key: 'construction', formType: 'construction_new_project', Icon: FaHardHat, nameKey: 'nav.construction' },
        { key: 'roofing', formType: 'roofing_new_project', Icon: FaHome, nameKey: 'nav.roofing' },
        { key: 'windows', formType: 'impact_windows_new_project', Icon: FaWindowMaximize, nameKey: 'nav.windows' },
    ]

    const hostname = window.location.hostname
    const pathname = window.location.pathname
    const detectedBU = BU_CONFIG.find(bu =>
        hostname.startsWith(bu.key) || pathname.startsWith(`/${bu.key}`)
    ) || null

    const openForm = (formType) => {
        preModalPath.current = window.location.pathname
        window.history.pushState({ modal: true }, '', FORM_PATHS[formType] || '/forms/architecture/new-quote')
        setActiveFormType(formType)
        setShowMondayForm(true)
    }

    const closeModal = () => {
        setShowMondayForm(false)
        window.history.replaceState(null, '', preModalPath.current || '/contact')
    }

    useEffect(() => {
        const handlePopState = () => setShowMondayForm(false)
        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [])
    const [formData, setFormData] = useState({})
    const [submitting, setSubmitting] = useState(false)
    const [showSuccess, setShowSuccess] = useState(false)

    const handleSubmit = (e) => {
        e.preventDefault()
        setSubmitting(true)
        // Handle form submission
        setTimeout(() => {
            setSubmitting(false)
            setShowSuccess(true)
        }, 2000)
    }

    const MAP_URLS = {
        texas: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3977.7464853870624!2d-96.80866752386214!3d32.80927338233608!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c1ffaabb00211%3A0xc9ecd02bf26a0fca!2sJD%20Rivero!5e1!3m2!1sen!2sve!4v1786550788577!5m2!1sen!2sve",
        florida: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3591.1396263529324!2d-80.34008772370903!3d25.831885877317785!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9b9a690e54617%3A0x8620ba038c7f2081!2s3750%20NW%2087th%20Ave%20Suite%20700%2C%20Doral%2C%20FL%2033178!5e0!3m2!1sen!2s!4v1739500000000!5m2!1sen!2s"
    }

    const GOOGLE_MAPS_LINKS = {
        texas: "https://maps.app.goo.gl/12xBXDPVgV4G2TAFA",
        florida: "https://maps.app.goo.gl/r4G1LbApczhWyURH9"
    }

    // Move arrays inside component to use translations
    const FORM_FIELDS = ['first_name', 'last_name', 'email', 'phone']

    // We will map these keys to form.residential, form.commercial, etc.
    const PROPERTY_TYPES = ['residential', 'commercial']
    const PROJECT_TYPES = ['new_construction', 'replacement']

    return (
        <>
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="overflow-hidden relative px-4 py-20 text-white bg-black sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1 }}
                        className="absolute inset-0 bg-neutral-900"
                    >
                        {/* Optional: Add a subtle pattern or background image if available */}
                        <div className="absolute inset-0 opacity-10 bg-[url('/construction/hero.webp')] bg-cover bg-center mix-blend-overlay"></div>
                    </motion.div>

                    <div className="relative z-10 mx-auto max-w-7xl text-center">
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="mb-6 text-5xl font-black tracking-tighter uppercase sm:text-6xl md:text-7xl"
                        >
                            {t('contact.hero_title')}
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="mx-auto max-w-3xl text-xl font-light text-gray-300 sm:text-2xl"
                        >
                            {t('contact.hero_desc')}
                        </motion.p>
                    </div>
                </section>

                {/* Main Content Grid */}
                <section className="px-4 py-20 mx-auto max-w-7xl sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 gap-16 items-start lg:grid-cols-2">

                        {/* Contact Info Column */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="order-2 space-y-12 lg:order-1"
                        >
                            <div>
                                <h2 className="mb-8 text-3xl font-bold tracking-wide uppercase">{t('contact.get_in_touch')}</h2>
                                <p className="mb-12 text-lg leading-relaxed text-gray-600">
                                    {t('contact.get_in_touch_desc')}
                                </p>

                                <div className="space-y-8">
                                    <div className="flex gap-4 items-start group">
                                        <div className="p-4 bg-gray-100 rounded-full transition-colors duration-300 group-hover:bg-primary">
                                            <FaPhone className="text-xl text-black" />
                                        </div>
                                        <div>
                                            <h3 className="mb-1 text-lg font-bold tracking-wider uppercase">{t('contact.phone')}</h3>
                                            <div className="space-y-2">
                                                <a href="tel:+12144620683" className="block text-lg text-gray-600 transition-colors hover:text-primary">
                                                    <span className="text-gray-500 font-bold text-[10px] mr-2 uppercase tracking-tighter">{t('regions.texas')}</span>
                                                    (214) 462 0683
                                                </a>
                                                <a href="tel:+17862961439" className="block text-lg text-gray-600 transition-colors hover:text-primary">
                                                    <span className="text-gray-500 font-bold text-[10px] mr-2 uppercase tracking-tighter">{t('regions.florida')}</span>
                                                    (786) 296 1439
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-4 items-start group">
                                        <div className="p-4 bg-gray-100 rounded-full transition-colors duration-300 group-hover:bg-primary">
                                            <FaEnvelope className="text-xl text-black" />
                                        </div>
                                        <div>
                                            <h3 className="mb-1 text-lg font-bold tracking-wider uppercase">{t('contact.email')}</h3>
                                            <a href="mailto:info@jdrivero.com" className="text-lg text-gray-600 transition-colors hover:text-primary">
                                                info@jdrivero.com
                                            </a>
                                        </div>
                                    </div>

                                    {/* Texas Office */}
                                    <div className="flex gap-4 items-start group">
                                        <div className="p-4 bg-gray-100 rounded-full transition-colors duration-300 group-hover:bg-primary">
                                            <FaMapMarkerAlt className="text-xl text-black" />
                                        </div>
                                        <div>
                                            <h3 className="mb-1 text-lg font-bold tracking-wider uppercase">{t('contact.office_texas')}</h3>
                                            <a
                                                href={GOOGLE_MAPS_LINKS.texas}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-lg text-gray-600 transition-colors hover:text-primary"
                                            >
                                                {t('contact.address_texas')}
                                            </a>
                                        </div>
                                    </div>

                                    {/* Florida Office */}
                                    <div className="flex gap-4 items-start group">
                                        <div className="p-4 bg-gray-100 rounded-full transition-colors duration-300 group-hover:bg-primary">
                                            <FaMapMarkerAlt className="text-xl text-black" />
                                        </div>
                                        <div>
                                            <h3 className="mb-1 text-lg font-bold tracking-wider uppercase">{t('contact.office_florida')}</h3>
                                            <a
                                                href={GOOGLE_MAPS_LINKS.florida}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block text-lg text-gray-600 transition-colors hover:text-primary"
                                            >
                                                {t('contact.address_florida')}
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                {/* Embedded Map */}
                                <div className="mt-6">
                                    <iframe
                                        src={MAP_URLS[activeMap]}
                                        width="100%"
                                        height="250"
                                        style={{ border: 0, borderRadius: '0.75rem' }}
                                        allowFullScreen=""
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        title={t('contact.map_title')}
                                    ></iframe>
                                </div>
                            </div>

                            <div className="p-8 bg-gray-50 rounded-3xl border border-gray-100">
                                <h3 className="mb-2 text-xl font-bold">{t('contact.quick_quote_title')}</h3>
                                <p className="mb-6 text-gray-600">{t('contact.quick_quote_desc')}</p>

                                {detectedBU ? (
                                    <button
                                        onClick={() => openForm(detectedBU.formType)}
                                        className="flex gap-3 justify-center items-center px-6 py-4 w-full text-sm font-black tracking-widest text-black uppercase rounded-xl transition-colors bg-primary hover:bg-yellow-400"
                                    >
                                        <detectedBU.Icon className="text-lg" />
                                        {t('contact.cta_quote')}
                                    </button>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        {BU_CONFIG.map((bu) => (
                                            <div
                                                key={bu.key}
                                                className="overflow-hidden rounded-xl border border-gray-200 group"
                                                onMouseEnter={() => setExpandedBU(bu.key)}
                                                onMouseLeave={() => setExpandedBU(null)}
                                            >
                                                <div className="flex justify-between items-center px-5 py-4 w-full text-sm font-bold tracking-widest text-black uppercase bg-white transition-colors cursor-default group-hover:bg-gray-50">
                                                    <span className="flex gap-3 items-center">
                                                        <bu.Icon className="text-base text-gray-600" />
                                                        {t(bu.nameKey)}
                                                    </span>
                                                    <motion.span
                                                        animate={{ rotate: expandedBU === bu.key ? 180 : 0 }}
                                                        transition={{ duration: 0.2 }}
                                                    >
                                                        <FaChevronDown className="text-xs text-gray-400" />
                                                    </motion.span>
                                                </div>
                                                <AnimatePresence initial={false}>
                                                    {expandedBU === bu.key && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.2 }}
                                                        >
                                                            <div className="px-4 pb-4">
                                                                <button
                                                                    onClick={() => openForm(bu.formType)}
                                                                    className="px-6 py-3 w-full text-sm font-black tracking-widest text-black uppercase rounded-lg transition-colors bg-primary hover:bg-yellow-400"
                                                                >
                                                                    {t('contact.cta_quote')}
                                                                </button>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Financing CTA */}
                                <div className="p-4 mt-4 text-center bg-yellow-50 rounded-xl border border-yellow-300">
                                    <p className="mb-2 text-sm font-semibold text-yellow-900">{t('contact.financing_question')}</p>
                                    <a
                                        href="https://www.hfsfinancial.net/promo/68fb9141f6eb8fc470725042/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex gap-2 items-center px-4 py-2 text-sm font-black tracking-widest text-black uppercase rounded-lg transition-colors bg-primary hover:bg-yellow-400"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {t('contact.get_financing')}
                                    </a>
                                </div>
                            </div>
                        </motion.div>

                        {/* Form Column - Matching ImpactWindows layout */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="bg-gray-50 p-8 sm:p-12 rounded-[2.5rem] shadow-xl order-1 lg:order-2"
                        >
                            <div className="mb-10">
                                <h2 className="mb-2 text-3xl font-black tracking-tighter uppercase">{t('contact.send_message')}</h2>
                                <div className="w-20 h-1.5 bg-primary"></div>
                            </div>

                            <form
                                action="https://formsubmit.co/info@jdrivero.com"
                                method="POST"
                                className="space-y-8"
                            >
                                <input type="hidden" name="_subject" value={t('contact.form_subject', 'New Contact Form Submission')} />
                                <input type="hidden" name="_template" value="table" />
                                <input type="hidden" name="_next" value="https://www.jdrivero.com/success" />
                                <input type="hidden" name="_captcha" value="false" />
                                <div className="space-y-6">
                                    {FORM_FIELDS.map((key, index) => (
                                        <motion.div
                                            key={key}
                                            initial={{ opacity: 0, y: 10 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.4, delay: index * 0.1 }}
                                        >
                                            <label className="block mb-2 text-xs font-bold tracking-widest text-gray-500 uppercase">{t(`form.${key}`)}</label>
                                            <input
                                                type={key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'}
                                                name={t(`form.${key}`).replace('*', '')}
                                                pattern={key === 'phone' ? "^[+]?[0-9 \\-]{10,15}$" : undefined}
                                                title={key === 'phone' ? t('contact.phone_error', 'Phone number must be between 10 and 15 digits.') : undefined}
                                                className="py-3 w-full text-lg bg-white border-b-2 border-gray-200 transition-colors focus:border-black focus:outline-none"
                                                required
                                            />
                                        </motion.div>
                                    ))}
                                </div>

                                <div className="pt-4 space-y-8">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: 0.4 }}
                                    >
                                        <p className="mb-4 text-xs font-bold tracking-widest text-gray-500 uppercase">{t('form.property_type')}</p>
                                        <div className="space-y-3">
                                            {PROPERTY_TYPES.map((key) => (
                                                <label key={key} className="flex gap-3 items-center cursor-pointer group">
                                                    <div className="flex relative justify-center items-center w-5 h-5 rounded-full border-2 border-gray-300 transition-colors group-hover:border-primary">
                                                        <input type="radio" name="propertyType" value={t(`form.${key}`)} className="absolute inset-0 w-full h-full appearance-none cursor-pointer peer" />
                                                        <div className="w-2.5 h-2.5 bg-primary rounded-full scale-0 peer-checked:scale-100 transition-transform" />
                                                    </div>
                                                    <span className="text-gray-700 transition-colors group-hover:text-black">{t(`form.${key}`)}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: 0.5 }}
                                    >
                                        <p className="mb-4 text-xs font-bold tracking-widest text-gray-500 uppercase">{t('form.project_type')}</p>
                                        <div className="space-y-3">
                                            {PROJECT_TYPES.map((key) => (
                                                <label key={key} className="flex gap-3 items-center cursor-pointer group">
                                                    <div className="flex relative justify-center items-center w-5 h-5 rounded-full border-2 border-gray-300 transition-colors group-hover:border-primary">
                                                        <input type="radio" name="projectType" value={t(`form.${key}`)} className="absolute inset-0 w-full h-full appearance-none cursor-pointer peer" />
                                                        <div className="w-2.5 h-2.5 bg-primary rounded-full scale-0 peer-checked:scale-100 transition-transform" />
                                                    </div>
                                                    <span className="text-gray-700 transition-colors group-hover:text-black">{t(`form.${key}`)}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: 0.6 }}
                                    >
                                        <p className="mb-4 text-xs font-bold tracking-widest text-gray-500 uppercase">{t('form.project_details')}</p>
                                        <textarea
                                            name={t('contact.project_details_label', 'Project Details')}
                                            className="py-3 w-full h-32 text-lg bg-white border-b-2 border-gray-200 transition-colors resize-none focus:border-black focus:outline-none"
                                            required
                                        />
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: 0.65 }}
                                    >
                                        <ConsentCheckbox />
                                    </motion.div>

                                    <motion.button
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.7 }}
                                        className="px-8 py-4 w-full font-black tracking-widest text-black uppercase rounded-lg shadow-lg transition-all bg-primary hover:bg-yellow-400 hover:shadow-xl hover:-translate-y-1"
                                    >
                                        {t('contact.send_message')}
                                    </motion.button>
                                </div>
                            </form>
                        </motion.div>

                    </div>
                </section>
            </div>

            {/* Monday Form Modal */}
            {showMondayForm && (
                <div className="overflow-y-auto fixed inset-0 z-50">
                    <div className="flex justify-center items-center p-4 min-h-screen">
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                            onClick={closeModal}
                        />

                        {/* Modal Content */}
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                            <div className="overflow-y-auto max-h-[90vh]">
                                <div className="flex sticky top-0 z-10 justify-between items-center px-6 py-4 bg-white border-b border-gray-200">
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        {t('contact.get_quote')}
                                    </h2>
                                    <button
                                        onClick={() => setShowMondayForm(false)}
                                        className="text-gray-400 transition-colors hover:text-gray-600"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                <MondayManagement
                                    session={null}
                                    onReturn={closeModal}
                                    isPublicMode={true}
                                    defaultFormType={activeFormType}
                                    thankYouPath={FORM_THANK_YOU_MAP[activeFormType]}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ContactPage
